import React, { useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Package, UtensilsCrossed, Flower2, ShoppingCart, Boxes,
  Plus, Minus, Trash2, MapPin, Navigation, Loader2, ShoppingBag
} from 'lucide-react';
import MapView from './MapView';

const SERVICE_TYPES = [
  { id: 'encomienda', name: 'Encomienda', icon: Package, color: '#FF007F', description: 'Envía un paquete' },
  { id: 'comida', name: 'Comida', icon: UtensilsCrossed, color: '#00F0FF', description: 'Pedidos de comida' },
  { id: 'flores', name: 'Flores', icon: Flower2, color: '#FF6B9D', description: 'Envío de flores' },
  { id: 'supermercado', name: 'Supermercado', icon: ShoppingCart, color: '#8A2BE2', description: 'Compra en super' },
  { id: 'varios', name: 'Varios', icon: Boxes, color: '#5CE9F5', description: 'Otros artículos' }
];

const DeliveryRequestModal = ({ open, onOpenChange, onOrderCreated }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: type, 2: items, 3: locations, 4: confirm
  const [serviceType, setServiceType] = useState(null);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: '', qty: 1, price: 0 });
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const totalPrice = items.reduce((sum, it) => sum + (it.qty * it.price), 0);
  const isEncomienda = serviceType === 'encomienda';

  const handleTypeSelect = (typeId) => {
    setServiceType(typeId);
    setStep(2);
  };

  const addItem = () => {
    if (!newItem.name.trim()) return;
    setItems((prev) => [...prev, { ...newItem, id: Date.now() }]);
    setNewItem({ name: '', qty: 1, price: 0 });
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateQty = (id, delta) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  };

  const handleSubmit = async () => {
    if (!pickup || !dropoff) return;
    setCreating(true);
    try {
      const response = await axios.post(`${API}/delivery/orders`, {
        client_id: user._id,
        service_type: serviceType,
        items: items,
        pickup_location: { lat: pickup.lat, lng: pickup.lng, address: pickup.address || 'Origen' },
        dropoff_location: { lat: dropoff.lat, lng: dropoff.lng, address: dropoff.address || 'Destino' },
        total_price: totalPrice,
        notes: notes
      }, { withCredentials: true });
      if (onOrderCreated) onOrderCreated(response.data);
      onOpenChange(false);
      // Reset
      setStep(1);
      setServiceType(null);
      setItems([]);
      setPickup(null);
      setDropoff(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear pedido');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="delivery-request-modal"
        className="bg-[#0B0C10] border-white/10 text-white max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-medium text-white" style={{ fontFamily: 'Outfit' }}>
            Solicitar Delivery
          </DialogTitle>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full transition-all duration-300 ${
                step >= s ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2]' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-[#9CA3AF]">¿Qué tipo de servicio necesitas?</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SERVICE_TYPES.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    data-testid={`delivery-type-${t.id}`}
                    onClick={() => handleTypeSelect(t.id)}
                    className="glass-card p-5 rounded-2xl hover:scale-[1.03] transition-all duration-300 group"
                  >
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 mx-auto"
                      style={{ background: `linear-gradient(135deg, ${t.color}40, ${t.color}20)` }}
                    >
                      <Icon className="w-7 h-7" style={{ color: t.color }} />
                    </div>
                    <p className="text-white font-medium text-center" style={{ fontFamily: 'Outfit' }}>{t.name}</p>
                    <p className="text-xs text-[#9CA3AF] text-center mt-1">{t.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {isEncomienda ? (
              <div className="glass-card p-5 rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-5 h-5 text-[#FF007F]" />
                  <p className="text-white font-medium">Detalles de la encomienda</p>
                </div>
                <Textarea
                  data-testid="delivery-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Describe el paquete: dimensiones, peso, contenido..."
                  className="bg-white/5 border-white/10 text-white rounded-xl"
                  rows={4}
                />
              </div>
            ) : (
              <>
                <div className="glass-card p-4 rounded-2xl space-y-3">
                  <p className="text-sm text-white/80 mb-2">Agrega artículos al carrito</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input
                      data-testid="delivery-item-name"
                      value={newItem.name}
                      onChange={(e) => setNewItem((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Nombre del artículo"
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                    />
                    <Input
                      type="number"
                      min="1"
                      value={newItem.qty}
                      onChange={(e) => setNewItem((p) => ({ ...p, qty: parseInt(e.target.value) || 1 }))}
                      placeholder="Cantidad"
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                    />
                    <div className="flex gap-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={newItem.price}
                        onChange={(e) => setNewItem((p) => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                        placeholder="Precio"
                        className="bg-white/5 border-white/10 text-white rounded-xl flex-1"
                      />
                      <Button
                        data-testid="delivery-add-item"
                        onClick={addItem}
                        className="gradient-button rounded-xl px-3"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Cart items */}
                {items.length > 0 && (
                  <div className="space-y-2">
                    {items.map((it) => (
                      <div key={it.id} className="glass-card p-3 rounded-xl flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-sm text-white">{it.name}</p>
                          <p className="text-xs text-[#9CA3AF]">${it.price.toFixed(2)} c/u</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateQty(it.id, -1)} className="p-1 bg-white/5 rounded">
                            <Minus className="w-3 h-3 text-white" />
                          </button>
                          <span className="text-white text-sm w-6 text-center">{it.qty}</span>
                          <button onClick={() => updateQty(it.id, 1)} className="p-1 bg-white/5 rounded">
                            <Plus className="w-3 h-3 text-white" />
                          </button>
                        </div>
                        <p className="text-sm text-white w-16 text-right">${(it.qty * it.price).toFixed(2)}</p>
                        <button
                          data-testid={`delivery-remove-${it.id}`}
                          onClick={() => removeItem(it.id)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Cart bar (always visible if items) */}
            {(items.length > 0 || isEncomienda) && (
              <div
                data-testid="delivery-cart-bar"
                className="glass-card p-4 rounded-2xl flex items-center justify-between border-2 border-[#FF007F]/30"
              >
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-[#FF007F]" />
                  <div>
                    <p className="text-sm text-white font-medium">
                      {isEncomienda ? 'Encomienda lista' : `${items.length} artículo${items.length !== 1 ? 's' : ''}`}
                    </p>
                    <p className="text-xs text-[#9CA3AF]">
                      {isEncomienda ? 'El precio lo negocia el delivery' : `Total: $${totalPrice.toFixed(2)}`}
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="delivery-continue-locations"
                  onClick={() => setStep(3)}
                  className="gradient-button rounded-xl"
                >
                  Continuar
                </Button>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={() => setStep(1)} className="bg-white/5 border border-white/10 rounded-xl text-white">
                Atrás
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#00F0FF]" />
              <Label className="text-white">
                {isEncomienda ? 'Ubicación de recolección del paquete' : 'Ubicación de recogida (tienda/local)'}
              </Label>
            </div>
            <MapView
              pickup={pickup}
              clickable={!dropoff}
              onLocationSelect={(pt) => setPickup({ ...pt, address: pt.address || `${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}` })}
              height="200px"
            />
            <Input
              placeholder="Dirección de recogida"
              value={pickup?.address || ''}
              onChange={(e) => setPickup((p) => ({ ...(p || {lat: -0.1807, lng: -78.4678}), address: e.target.value }))}
              className="bg-white/5 border-white/10 text-white rounded-xl"
            />

            <div className="flex items-center gap-2 mt-3">
              <Navigation className="w-4 h-4 text-[#FF007F]" />
              <Label className="text-white">
                {isEncomienda ? 'Ubicación de entrega' : 'Ubicación de entrega a domicilio'}
              </Label>
            </div>
            <MapView
              pickup={pickup}
              dropoff={dropoff}
              clickable={!!pickup}
              onLocationSelect={(pt) => setDropoff({ ...pt, address: pt.address || `${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}` })}
              height="200px"
            />
            <Input
              placeholder="Dirección de entrega"
              value={dropoff?.address || ''}
              onChange={(e) => setDropoff((p) => ({ ...(p || {lat: -0.2295, lng: -78.5243}), address: e.target.value }))}
              className="bg-white/5 border-white/10 text-white rounded-xl"
            />

            <div className="flex gap-2 mt-3">
              <Button onClick={() => setStep(2)} className="bg-white/5 border border-white/10 rounded-xl text-white">
                Atrás
              </Button>
              <Button
                data-testid="delivery-confirm-locations"
                onClick={() => setStep(4)}
                disabled={!pickup || !dropoff}
                className="flex-1 gradient-button rounded-xl py-5"
              >
                Revisar pedido
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="glass-card p-4 rounded-2xl space-y-3">
              <p className="text-xs text-[#9CA3AF] uppercase tracking-widest">Resumen del pedido</p>
              <div className="text-sm text-white">
                <p><span className="text-[#9CA3AF]">Tipo:</span> {SERVICE_TYPES.find(s => s.id === serviceType)?.name}</p>
                <p><span className="text-[#9CA3AF]">Recogida:</span> {pickup?.address}</p>
                <p><span className="text-[#9CA3AF]">Entrega:</span> {dropoff?.address}</p>
                {!isEncomienda && <p><span className="text-[#9CA3AF]">Artículos:</span> {items.length}</p>}
                {!isEncomienda && <p><span className="text-[#9CA3AF]">Total:</span> ${totalPrice.toFixed(2)}</p>}
              </div>
            </div>

            <MapView pickup={pickup} dropoff={dropoff} height="200px" />

            <div className="flex gap-2">
              <Button onClick={() => setStep(3)} className="bg-white/5 border border-white/10 rounded-xl text-white">
                Atrás
              </Button>
              <Button
                data-testid="delivery-submit-button"
                onClick={handleSubmit}
                disabled={creating}
                className="flex-1 gradient-button rounded-xl py-5"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Pedido'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeliveryRequestModal;
